import React from "react";
import { Loader2, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { useTranslations } from "use-intl";

export interface FileUploadStatus {
  name: string;
  status: "uploading" | "uploaded" | "success" | "error";
  error?: string;
  documentStatus?: string;
  extractionMetadata?: { [key: string]: any } | null;
}

interface UploadProgressProps {
  files: FileUploadStatus[];
}

const statusIcon = (status: FileUploadStatus["status"]) => {
  switch (status) {
    case "uploading":
    case "uploaded":
      return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
    case "success":
      return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    case "error":
      return <XCircle className="h-4 w-4 text-red-500" />;
  }
};

const UploadProgress: React.FC<UploadProgressProps> = ({ files }) => {
  const t = useTranslations("consultantUpload.progress");
  if (files.length === 0) return null;

  return (
    <ul className="space-y-2">
      {files.map((file, idx) => (
        <li key={idx} className="flex items-start gap-2 text-sm">
          {statusIcon(file.status)}
          <div className="min-w-0 flex-1">
            <span className="block truncate">{file.name}</span>
            {file.status === "error" && file.error && (
              <span className="block text-xs text-red-600">{file.error}</span>
            )}
            {file.documentStatus === "rejected" && (() => {
              let duplicateInfo: { vendor?: string } | null = null;
              let rejectionMessage: string | null = null;
              try {
                const raw = file.extractionMetadata?.finalizationError;
                if (raw) {
                  const parsed = JSON.parse(raw);
                  if (parsed.reason === 'DUPLICATE_DOCUMENT') duplicateInfo = parsed.duplicateOf;
                  else if (parsed.reason === 'UNSUPPORTED_DOCUMENT_TYPE') rejectionMessage = t("unsupportedType");
                  else if (parsed.reason === 'POOR_IMAGE_QUALITY') rejectionMessage = t("poorImage");
                }
              } catch { /* plain string or unparseable — use generic label */ }

              return (
                <div className="flex items-center gap-2 text-destructive text-sm">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span>
                    {duplicateInfo
                      ? t("duplicateOf", { vendor: duplicateInfo.vendor ?? t("existingDocument") })
                      : rejectionMessage ?? t("rejected")}
                  </span>
                </div>
              );
            })()}
          </div>
        </li>
      ))}
    </ul>
  );
};

export default UploadProgress;
